<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsDashboardCard :disabled="disabled" :dark="$vuetify.theme.dark" @resize="expanded => $emit('resize', expanded)">
    <div class="title-container">
      <div v-once class="card-title">
        {{ $t('queueSummary') }}
      </div>

      <v-spacer />

      <div class="bp-selection">
        <v-select
          dense
          class="bp-select"
          :items="businessProcessesStats"
          :label="$t('businessProcess')"
          item-value="businessProcessId"
          item-text="businessProcessName"
          :value="selectedBusinessProcessId"
          :disabled="!!onlyForBusinessProcessId"
          @change="changeSelectedBP"
        />
      </div>
    </div>

    <div class="stats-container">
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessWaitingInteractions.value, 'number') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('liveInteractionsWaiting') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessWaitingDeferrableInteractions.value, 'number') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('deferrableInteractionsWaiting') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count" :live-dot="waitingInteractions">
          <ecs-live-time-ago v-if="waitingInteractions" :since="longestWaiting" short-output verbose />
          <span v-else>0s</span>
        </div>
        <div v-once class="total-stat-description">
          {{ $t('longestWaitingLiveInteraction') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.totalReadyAgents, 'number') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('readyAgents') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessServiceLevel.value, 'percentage') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('serviceLevel') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessAverageAnswerTime.value, 'duration') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('averageSpeedOfAnswer') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessAnsweredInteractions.value, 'number') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('interactionsAnswered') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessAbandonedInteractions.value, 'number') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('interactionsAbandoned') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessOverflowInteractions.value, 'number') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('interactionsOverflowed') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessAbandonedPercentage.value, 'percentage') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('abandonRate') }}
        </div>
      </div>
      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat(selectedBusinessProcess.businessProcessAverageAgentHandlingTime.value, 'duration') }}
        </div>
        <div v-once class="total-stat-description">
          {{ $t('averageHandleTime') }}
        </div>
      </div>

      <div class="clear"></div>
    </div>

    <div class="clear"></div>

    <div class="footer-info">
      {{ $t('lastReset') }}
      {{ lastReset }}
    </div>
  </EcsDashboardCard>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EcsDashboardCard from '@/components/ecs-dashboard-card/EcsDashboardCard';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import BusinessProcess from '@/classes/BusinessProcess';
import EcsLiveTimeAgo from '@/components/ecs-live-time-ago/EcsLiveTimeAgo';

const DEFAULT_BP = {
  businessProcessLoggedInAgents: {},
  businessProcessWaitingInteractions: {},
  businessProcessWaitingDeferrableInteractions: {},
  businessProcessLongestWaitingTime: {},
  businessProcessServiceLevel: {},
  businessProcessAverageAnswerTime: {},
  businessProcessAnsweredInteractions: {},
  businessProcessAbandonedInteractions: {},
  businessProcessOverflowInteractions: {},
  businessProcessAbandonedPercentage: {},
  businessProcessAverageAgentHandlingTime: {}
};

export default {
  name: 'EcsQueueSummaryCard',

  components: {
    EcsLiveTimeAgo,
    EcsDashboardCard
  },

  mixins: [EcsKpiFormatter],

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    onlyForBusinessProcessId: {
      type: String,
      default: ''
    },
    startBusinessProcessId: {
      type: String,
      required: true,
      default: ''
    }
  },

  data() {
    return {
      dataset: [],
      expanded: false,
      search: '',
      selectedBusinessProcessId: ''
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    assignedBps() {
      const assignedBPIds = this.loggedInAgent.assignedBusinessProcessIds;
      const onlyAssignedBPs = bp => assignedBPIds.includes(bp.businessProcessId);
      return this.$store.state.STATS.BUSINESS_PROCESSES.filter(onlyAssignedBPs);
    },

    businessProcessesStats() {
      const assignedBusinessProcess = this.assignedBps;
      if (this.onlyForBusinessProcessId) {
        const onlySelectedBp = bp => bp.businessProcessId === this.onlyForBusinessProcessId;
        const bp = assignedBusinessProcess.find(onlySelectedBp);
        return bp ? [bp] : [DEFAULT_BP];
      } else {
        return assignedBusinessProcess.sort(BusinessProcess.compare) || [DEFAULT_BP];
      }
    },

    lastReset() {
      return moment((this.selectedBusinessProcess || {}).lastResetTime).fromNow();
    },

    waitingInteractions() {
      return !!this.getBpProp('businessProcessWaitingInteractions');
    },

    longestWaiting() {
      return this.getBpProp('businessProcessLongestWaitingTime');
    },

    selectedBusinessProcess() {
      const bp = this.businessProcessesStats.find(bp => bp.businessProcessId === this.selectedBusinessProcessId);
      if (!bp) {
        return DEFAULT_BP;
      }
      return new BusinessProcess(bp);
    }
  },

  watch: {
    businessProcessesStats() {
      this.initialize();
    }
  },

  mounted() {
    this.initialize();
    this.selectedBusinessProcessId = this.startBusinessProcessId;
  },

  methods: {
    changeSelectedBP(value) {
      this.selectedBusinessProcessId = value;
      this.$emit('saveCardStartingBusinessProcess', value);
    },

    getBpProp(key) {
      if (!this.selectedBusinessProcess) {
        return null;
      }
      return (this.selectedBusinessProcess[key] || {}).value;
    },

    getFirstBpId() {
      return this.businessProcessesStats[0].businessProcessId;
    },

    async initialize() {
      if (!this.businessProcessesStats.length) {
        return;
      }
      if (!this.selectedBusinessProcessId || this.selectedBusinessProcessId.length === 0) {
        if (this.onlyForBusinessProcessId && this.onlyForBusinessProcessId.length > 0) {
          this.changeSelectedBP(this.onlyForBusinessProcessId);
        } else {
          this.changeSelectedBP(this.getFirstBpId());
        }
      }
      await this.$nextTick();
    }
  }
};
</script>

<style src="./scoped.css" scoped></style>
